import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Medal, Plus, Check, X, Trophy, Upload, FileText, Pencil, Trash2, Award } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Avatar, ProgressBar } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function rankPct(total, cur, nxt) {
  if (!nxt) return 100;
  const span = nxt.points - cur.points;
  if (span <= 0) return 100;
  return Math.min(100, Math.round(((total - cur.points) / span) * 100));
}

function RankCard({ p }) {
  if (!p) return null;
  return (
    <div data-testid="nhsda-rank-card" className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Current Rank</p>
          <p className="font-head text-3xl font-extrabold text-teal mt-1 flex items-center gap-2"><Trophy className="w-7 h-7" />{p.current_rank?.name}</p>
        </div>
        <div className="text-right">
          <p className="font-head text-4xl font-extrabold">{p.total_points}</p>
          <p className="text-xs text-muted-foreground">points</p>
        </div>
      </div>
      <div className="mt-5">
        <ProgressBar value={rankPct(p.total_points, p.current_rank, p.next_rank)} />
        <p className="text-sm text-muted-foreground mt-2">
          {p.next_rank ? `${p.to_next} points to ${p.next_rank.name}` : "Top rank reached! 🎉"}
          {p.pending_count > 0 ? ` · ${p.pending_count} pending review` : ""}
        </p>
      </div>
    </div>
  );
}

function EntryList({ entries }) {
  if (!entries?.length) return <EmptyState icon={Award} title="No point entries yet" hint="Approved activities will appear here." />;
  return (
    <div className="space-y-3">
      {entries.map((e) => (
        <div key={e.id} data-testid={`nhsda-entry-${e.id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold">{e.category_name} <span className="text-teal-dark font-bold">+{e.points}</span></p>
            {e.description && <p className="text-sm text-muted-foreground">{e.description}</p>}
            <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
              {e.activity_date && <span>{e.activity_date}</span>}
              {(e.evidence_url || e.evidence_file_id) && <a className="text-teal-dark font-semibold flex items-center gap-1" href={e.evidence_url || `${BACKEND}/api/files/${e.evidence_file_id}`} target="_blank" rel="noreferrer"><FileText className="w-3.5 h-3.5" />Evidence</a>}
            </div>
          </div>
          <Badge className={`rounded-full text-[10px] shrink-0 ${e.status === "Approved" ? "bg-teal text-white" : e.status === "Denied" ? "bg-red-100 text-red-700" : "bg-yellow-50 text-yellow-700"}`}>{e.status}</Badge>
        </div>
      ))}
    </div>
  );
}

export default function NHSDA() {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const isStaff = isDirector || user?.role === "teacher";
  const [cats, setCats] = useState([]);
  const [members, setMembers] = useState({ members: [], pending_total: 0 });
  const [dancers, setDancers] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  // dancer/parent
  const [selfDancers, setSelfDancers] = useState([]);
  const [activeDancer, setActiveDancer] = useState(null);
  const [prog, setProg] = useState(null);

  const loadStaff = () => {
    api.get("/nhsda/categories").then((r) => setCats(r.data));
    api.get("/nhsda/members").then((r) => setMembers(r.data));
    api.get("/people/dancers").then((r) => setDancers(r.data)).catch(() => {});
    api.get("/nhsda/entries").then((r) => setAllEntries(r.data)).catch(() => {});
  };
  const loadSelf = () => api.get("/people/dancers").then((r) => { setSelfDancers(r.data); if (r.data[0]) setActiveDancer(r.data[0].id); });
  useEffect(() => { if (isStaff) loadStaff(); else loadSelf(); }, []);
  useEffect(() => { if (!isStaff && activeDancer) api.get(`/nhsda/progress/${activeDancer}`).then((r) => setProg(r.data)); }, [activeDancer]);

  const dancerMap = Object.fromEntries(dancers.map((d) => [d.id, d]));
  const pending = allEntries.filter((e) => e.status === "Pending");

  // log entry
  const empty = { dancer_id: "", category_id: "", description: "", activity_date: "", evidence_url: "" };
  const [logDlg, setLogDlg] = useState(false);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const submitEntry = async () => {
    if (!form.dancer_id || !form.category_id) { toast.error("Choose a dancer and category"); return; }
    try {
      let payload = { ...form };
      if (file) { const fd = new FormData(); fd.append("file", file); const r = await api.post("/files", fd); payload.evidence_file_id = r.data.id; }
      await api.post("/nhsda/entries", payload);
      toast.success(isDirector ? "Points added" : "Sent for approval");
      setLogDlg(false); setForm(empty); setFile(null); loadStaff();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const review = async (id, action) => {
    try { await api.post(`/nhsda/entries/${id}/review`, { action }); toast.success(action === "approve" ? "Approved 🎉" : "Denied"); loadStaff(); }
    catch (e) { toast.error("Error"); }
  };

  // categories
  const emptyCat = { name: "", description: "", points: 5, active: true };
  const [catDlg, setCatDlg] = useState(false);
  const [catForm, setCatForm] = useState(emptyCat);
  const [catEditId, setCatEditId] = useState(null);
  const saveCat = async () => {
    try {
      if (catEditId) await api.put(`/nhsda/categories/${catEditId}`, catForm);
      else await api.post("/nhsda/categories", catForm);
      toast.success("Category saved"); setCatDlg(false); setCatForm(emptyCat); setCatEditId(null); api.get("/nhsda/categories").then((r) => setCats(r.data));
    } catch (e) { toast.error("Error"); }
  };
  const delCat = async (c) => { await api.delete(`/nhsda/categories/${c.id}`); toast.success("Removed"); api.get("/nhsda/categories").then((r) => setCats(r.data)); };

  // ---- Dancer / Parent view ----
  if (!isStaff) {
    const activeMember = selfDancers.find((d) => d.id === activeDancer)?.nhsda_member;
    return (
      <div className="stagger-in">
        <PageHeader title="Honor Society" subtitle="Your NHSDA points and rank progress." testid="nhsda-page" />
        {selfDancers.length > 1 && <div className="flex flex-wrap gap-2 mb-4">
          {selfDancers.map((d) => <button key={d.id} data-testid={`nhsda-dancer-${d.id}`} onClick={() => setActiveDancer(d.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border ${activeDancer === d.id ? "bg-black text-white border-black" : "bg-white"}`}>{d.first_name} {d.last_name}</button>)}
        </div>}
        {!activeMember ? <EmptyState icon={Medal} title="Not an Honor Society member yet" hint="Ask your director about joining NHSDA to start earning points." testid="nhsda-not-member" /> :
          !prog ? <EmptyState icon={Medal} title="No NHSDA record yet" /> : <div className="space-y-6">
            <RankCard p={prog} />
            <div><h3 className="font-head font-bold text-lg mb-3">Point history</h3><EntryList entries={prog.entries} /></div>
          </div>}
      </div>
    );
  }

  // ---- Staff view ----
  return (
    <div className="stagger-in">
      <PageHeader title="NHSDA Honor Society" subtitle="Track points, evidence and rank progress for your members." testid="nhsda-page"
        action={<Button data-testid="log-points-button" onClick={() => { setForm({ ...empty, category_id: cats[0]?.id || "" }); setLogDlg(true); }} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />Log Points</Button>} />

      <Tabs defaultValue="members">
        <TabsList className="bg-white rounded-full p-1 mb-6 flex-wrap h-auto">
          <TabsTrigger value="members" data-testid="nhsda-tab-members" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Members</TabsTrigger>
          {isDirector && <TabsTrigger value="review" data-testid="nhsda-tab-review" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Review{members.pending_total > 0 && ` (${members.pending_total})`}</TabsTrigger>}
          {isDirector && <TabsTrigger value="categories" data-testid="nhsda-tab-categories" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Categories</TabsTrigger>}
        </TabsList>

        <TabsContent value="members">
          {members.members.length === 0 ? <EmptyState icon={Medal} title="No honor society members" hint="Mark a dancer's membership in People to include them here." /> :
            <div className="space-y-3">
              {members.members.map((m) => (
                <div key={m.dancer_id} data-testid={`nhsda-member-${m.dancer_id}`} className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-center gap-3">
                    <Avatar first={m.first_name} last={m.last_name} size={44} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-head font-bold">{m.first_name} {m.last_name}</p>
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-teal-dark"><Trophy className="w-4 h-4" />{m.current_rank?.name} · {m.total_points} pts</span>
                      </div>
                      <p className="text-xs text-muted-foreground capitalize">{m.membership} member{m.pending_count > 0 ? ` · ${m.pending_count} pending` : ""}</p>
                    </div>
                  </div>
                  <div className="mt-3"><ProgressBar value={rankPct(m.total_points, m.current_rank, m.next_rank)} height={10} />
                    <p className="text-xs text-muted-foreground mt-1.5">{m.next_rank ? `${m.to_next} pts to ${m.next_rank.name}` : "Top rank!"}</p></div>
                </div>
              ))}
            </div>}
        </TabsContent>

        {isDirector && <TabsContent value="review">
          {pending.length === 0 ? <EmptyState icon={Check} title="Nothing to review" hint="Teacher-submitted entries appear here." /> :
            <div className="space-y-3">
              {pending.map((e) => {
                const d = dancerMap[e.dancer_id];
                return (
                  <div key={e.id} data-testid={`nhsda-review-${e.id}`} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-semibold">{d ? `${d.first_name} ${d.last_name}` : "Dancer"} · {e.category_name} <span className="text-teal-dark font-bold">+{e.points}</span></p>
                      <p className="text-sm text-muted-foreground">{e.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">by {e.submitted_by_name}{e.activity_date ? ` · ${e.activity_date}` : ""}{(e.evidence_url || e.evidence_file_id) ? "" : ""}</p>
                      {(e.evidence_url || e.evidence_file_id) && <a className="text-xs text-teal-dark font-semibold flex items-center gap-1 mt-1" href={e.evidence_url || `${BACKEND}/api/files/${e.evidence_file_id}`} target="_blank" rel="noreferrer"><FileText className="w-3.5 h-3.5" />View evidence</a>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" data-testid={`nhsda-approve-${e.id}`} onClick={() => review(e.id, "approve")} className="rounded-full bg-teal hover:bg-teal-dark"><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" data-testid={`nhsda-deny-${e.id}`} onClick={() => review(e.id, "deny")} className="rounded-full"><X className="w-4 h-4" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>}
        </TabsContent>}

        {isDirector && <TabsContent value="categories">
          <div className="flex justify-end mb-4"><Button data-testid="add-nhsda-category" onClick={() => { setCatForm(emptyCat); setCatEditId(null); setCatDlg(true); }} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />New Category</Button></div>
          <div className="space-y-2">
            {cats.map((c) => (
              <div key={c.id} data-testid={`nhsda-category-${c.id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-teal-light flex items-center justify-center font-head font-bold text-teal-dark">{c.points}</span>
                <div className="flex-1 min-w-0"><p className="font-semibold">{c.name}{c.active === false && <span className="text-xs text-muted-foreground ml-2">(inactive)</span>}</p><p className="text-xs text-muted-foreground truncate">{c.description}</p></div>
                <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0" data-testid={`edit-nhsda-category-${c.id}`} onClick={() => { setCatForm({ ...emptyCat, ...c }); setCatEditId(c.id); setCatDlg(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-nhsda-category-${c.id}`} onClick={() => delCat(c)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>}
      </Tabs>

      {/* Log points dialog */}
      <Dialog open={logDlg} onOpenChange={setLogDlg}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Medal className="w-5 h-5 text-teal" />Log NHSDA Points</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Dancer</Label>
                <select data-testid="nhsda-entry-dancer" className="w-full border rounded-md h-10 px-3" value={form.dancer_id} onChange={(e) => setForm({ ...form, dancer_id: e.target.value })}>
                  <option value="">Select…</option>{dancers.map((d) => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
                </select></div>
              <div className="space-y-1.5"><Label>Category</Label>
                <select data-testid="nhsda-entry-category" className="w-full border rounded-md h-10 px-3" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                  {cats.filter((c) => c.active !== false).map((c) => <option key={c.id} value={c.id}>{c.name} (+{c.points})</option>)}
                </select></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="nhsda-entry-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What did they do?" /></div>
            <div className="space-y-1.5"><Label>Activity date</Label><Input data-testid="nhsda-entry-date" type="date" value={form.activity_date} onChange={(e) => setForm({ ...form, activity_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Evidence link (optional)</Label><Input data-testid="nhsda-entry-url" value={form.evidence_url} onChange={(e) => setForm({ ...form, evidence_url: e.target.value })} placeholder="https://…" /></div>
            <div>
              <input ref={fileRef} data-testid="nhsda-evidence-file" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()}><Upload className="w-3.5 h-3.5 mr-1" />{file ? "Change file" : "Upload evidence (image/PDF)"}</Button>
              <span className="text-xs text-muted-foreground ml-2">{file?.name}</span>
            </div>
          </div>
          <DialogFooter><Button data-testid="submit-nhsda-entry" onClick={submitEntry} className="rounded-full bg-teal hover:bg-teal-dark">{isDirector ? "Add Points" : "Submit for Approval"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category dialog */}
      <Dialog open={catDlg} onOpenChange={setCatDlg}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-head">{catEditId ? "Edit" : "New"} Point Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="nhsda-category-name" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Points per entry</Label><Input data-testid="nhsda-category-points" type="number" value={catForm.points} onChange={(e) => setCatForm({ ...catForm, points: parseInt(e.target.value || "0", 10) })} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="save-nhsda-category" onClick={saveCat} className="rounded-full bg-teal hover:bg-teal-dark">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
